import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Header.css';
import { useTheme } from '../contexts/ThemeContext';
import { translations, SupportedLanguage } from '../i18n/translations';
import { portalTranslations } from '../i18n/portalTranslations';
import { useLanguage } from '../contexts/LanguageContext';

const unlockBodyScroll = () => {
  document.body.classList.remove('menu-open');
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  document.body.style.height = '';
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[language];
  const pt = portalTranslations[language];

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    unlockBodyScroll();
  }, []);

  const openMenu = useCallback(() => {
    setIsMenuOpen(true);
    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';
  }, []);

  const toggleMenu = useCallback(() => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [closeMenu, isMenuOpen, openMenu]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, location.hash, language, closeMenu]);

  useEffect(() => {
    if (!isMenuOpen) {
      unlockBodyScroll();
      return undefined;
    }

    document.body.classList.add('menu-open');
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      unlockBodyScroll();
    };
  }, [isMenuOpen, closeMenu]);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    closeMenu();

    if (location.pathname !== '/') {
      navigate({ pathname: '/', hash: href.replace('#', '') });
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      const headerHeight = 70;
      const top = element.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [closeMenu, location.pathname, navigate]);

  const handleLanguageChange = (nextLanguage: SupportedLanguage) => {
    closeMenu();
    setLanguage(nextLanguage);
  };

  return (
    <header className={`header ${isScrolled ? 'is-scrolled' : ''}`}>
      {isMenuOpen && (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="إغلاق القائمة"
          onClick={closeMenu}
        />
      )}
      <nav className="navbar">
        <Link to="/" className="logo" onClick={closeMenu}>
          <img
            src={encodeURI('/شعار صفحة .png')}
            alt="شعار الشراع للتخليص الجمركي"
            className="logo-image"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/%D8%B4%D8%B9%D8%A7%D8%B1%20%D8%B5%D9%81%D8%AD%D8%A9%20.png';
            }}
          />
          <span className="logo-text">الشراع</span>
        </Link>
        <ul className={`nav-links ${isMenuOpen ? 'nav-open' : ''}`} id="primary-navigation">
          <li><a href="#home" onClick={(e) => handleNavClick(e, '#home')}>{t.navigation.home}</a></li>
          <li><a href="#about" onClick={(e) => handleNavClick(e, '#about')}>{t.navigation.about}</a></li>
          <li><Link to="/shop" onClick={closeMenu}>المنزل الذكي</Link></li>
          <li><Link to="/credentials" onClick={closeMenu}>{t.navigation.credentials}</Link></li>
          <li><a href="#services" onClick={(e) => handleNavClick(e, '#services')}>{t.navigation.services}</a></li>
          <li><a href="#tracker" onClick={(e) => handleNavClick(e, '#tracker')}>{pt.navPortal}</a></li>
          <li><a href="#borders" onClick={(e) => handleNavClick(e, '#borders')}>{t.navigation.borders}</a></li>
          <li><a href="#team" onClick={(e) => handleNavClick(e, '#team')}>{t.navigation.team}</a></li>
          <li><a href="#contact" onClick={(e) => handleNavClick(e, '#contact')}>{t.navigation.contact}</a></li>
        </ul>
        <div className="header-actions">
          <select
            className="language-select"
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
            aria-label="Change language"
          >
            <option value="ar">AR</option>
            <option value="en">EN</option>
            <option value="de">DE</option>
          </select>
          <button
            className="theme-toggle"
            onClick={toggleDarkMode}
            aria-label={isDarkMode ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع الداكن'}
            type="button"
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <button
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            type="button"
            aria-label="قائمة التنقل"
            aria-expanded={isMenuOpen}
            aria-controls="primary-navigation"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>
    </header>
  );
};

export default Header;
